/**
 * Schedules incoming TTS audio chunks (raw PCM16 mono, matching
 * Broker/voice/gemini_live_provider.py's TTS_SAMPLE_RATE = 24kHz) back to
 * back via Web Audio API, and supports immediate interruption for barge-in.
 */
const TTS_SAMPLE_RATE = 24000;

export class PlaybackQueue {
  constructor(sampleRate = TTS_SAMPLE_RATE) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    this._ctx = new Ctx();
    this._sampleRate = sampleRate;
    this._nextStartTime = 0;
    this._sources = [];
  }

  _toAudioBuffer(arrayBuffer) {
    const int16 = new Int16Array(arrayBuffer);
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) {
      const s = int16[i];
      float32[i] = s / (s < 0 ? 0x8000 : 0x7fff);
    }
    const buffer = this._ctx.createBuffer(1, float32.length, this._sampleRate);
    buffer.copyToChannel(float32, 0);
    return buffer;
  }

  /** Queue one chunk of raw PCM16 audio for gapless playback. */
  enqueue(arrayBuffer) {
    if (this._ctx.state === 'suspended') {
      this._ctx.resume();
    }
    const audioBuffer = this._toAudioBuffer(arrayBuffer);
    const source = this._ctx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(this._ctx.destination);

    const startAt = Math.max(this._ctx.currentTime, this._nextStartTime);
    source.start(startAt);
    this._nextStartTime = startAt + audioBuffer.duration;

    this._sources.push(source);
    source.onended = () => {
      this._sources = this._sources.filter((s) => s !== source);
    };
  }

  /** True while audio is still scheduled to be coming out of the speakers.
   * Used to gate the mic against echo — see pcm-worklet.js. */
  isPlaying() {
    return this._nextStartTime > this._ctx.currentTime;
  }

  /** Barge-in: stop whatever's playing right now and drop anything queued. */
  clear() {
    this._sources.forEach((s) => {
      s.onended = null;
      try {
        s.stop();
      } catch (_) {
        /* already stopped/ended */
      }
    });
    this._sources = [];
    this._nextStartTime = this._ctx.currentTime;
  }

  close() {
    this.clear();
    this._ctx.close();
  }
}
